import React from 'react';
import { Box, Typography, Button, Grid } from '@mui/material';

interface ProjectGraphCardProps {
    item: any;
    onBack?: () => void;
    onPeriodClick?: () => void;
    onAccumulatedClick?: () => void;
    selectedMode?: 'period' | 'accumulated';
    hideButtons?: boolean;
}

export const ProjectGraphCard: React.FC<ProjectGraphCardProps> = ({
    item, onPeriodClick, onAccumulatedClick, selectedMode, hideButtons, onBack
}) => {

    const fmt = (val: number | undefined) => val !== undefined ? Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val) : '0.00';
    const fmtP = (val: number | undefined) => val !== undefined ? `${val}%` : '0%';

    const labelStyle = { fontWeight: 'bold', fontSize: '0.85rem', color: '#555' };
    const renderCell = (val: any, color: string = '#333', bold: boolean = false) => (
        <Typography sx={{ fontSize: '0.85rem', color, fontWeight: bold ? 'bold' : 'normal', lineHeight: '1.2', mb: 0.5 }}>
            {val}
        </Typography>
    );

    return (
        <Box bgcolor="white" p={2} borderRadius={2} border="1px solid #e0e0e0" mb={1} boxShadow={1}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography variant="subtitle1" fontWeight="bold" color="#333">
                    {item.Periodo || '---'}
                </Typography>

                {!hideButtons && (
                    <Box display="flex" gap={1}>
                        {selectedMode && <Button
                            variant={'outlined'}
                            color="primary"
                            onClick={onBack}
                            sx={{
                                padding: '1px 8px',
                                minHeight: 'unset',
                                height: '24px',
                                fontSize: '0.7rem',
                                textTransform: 'none'
                            }}
                        >
                            Volver
                        </Button>}
                        {selectedMode !== 'period' && <Button
                            variant={'outlined'}
                            color="primary"
                            onClick={onPeriodClick}
                            sx={{
                                padding: '1px 8px',
                                minHeight: 'unset',
                                height: '24px',
                                fontSize: '0.7rem',
                                textTransform: 'none'
                            }}
                        >
                            Recursos del período
                        </Button>}
                        {selectedMode !== 'accumulated' && <Button
                            variant={'outlined'}
                            color="primary"
                            onClick={onAccumulatedClick}
                            sx={{
                                padding: '1px 8px',
                                minHeight: 'unset',
                                height: '24px',
                                fontSize: '0.7rem',
                                textTransform: 'none'
                            }}
                        >
                            Recursos acumulado
                        </Button>}
                    </Box>
                )}
            </Box>

            <Grid container spacing={0} sx={{ textAlign: 'center' }}>

                <Grid item xs={2.4}>
                    <Box display="flex" justifyContent="center" alignItems="center" gap={0.5} mb={1}>
                        <Box width={8} height={8} borderRadius="50%" bgcolor="#2980b9" />
                        <Typography sx={labelStyle}>Programado</Typography>
                    </Box>
                    {renderCell(fmt(item.M_ProgramadoValorizado))}
                    {renderCell(fmtP(item.PorcentajeProgramado))}
                    {renderCell(fmt(item.M_ProgramadoValorizadoAcumulado), '#333', true)}
                    {renderCell(fmtP(item.PorcentajeProgramadoAcumulado), '#333', true)}
                </Grid>

                <Grid item xs={2.4}>
                    <Box display="flex" justifyContent="center" alignItems="center" gap={0.5} mb={1}>
                        <Box width={8} height={8} borderRadius="50%" bgcolor="#f39c12" />
                        <Typography sx={labelStyle}>Valorizado</Typography>
                    </Box>
                    {renderCell(fmt(item.M_ValorizadoValorizado), '#c0392b')}
                    {renderCell(fmtP(item.PorcentajeValorizado), '#c0392b')}
                    {renderCell(fmt(item.M_ValorizadoValorizadoAcumulado), '#c0392b', true)}
                    {renderCell(fmtP(item.PorcentajeValorizadoAcumulado), '#c0392b', true)}
                </Grid>

                <Grid item xs={2.4}>
                    <Box display="flex" justifyContent="center" alignItems="center" gap={0.5} mb={1}>
                        <Box width={8} height={8} borderRadius="50%" bgcolor="#1abc9c" />
                        <Typography sx={labelStyle}>Real</Typography>
                    </Box>
                    {renderCell(fmt(item.M_RealValorizado), '#c0392b')}
                    {renderCell(fmtP(item.PorcentajeReal), '#c0392b')}
                    {renderCell(fmt(item.M_RealValorizadoAcumulado), '#27ae60', true)}
                    {renderCell(fmtP(item.PorcentajeRealAcumulado), '#27ae60', true)}
                </Grid>

                <Grid item xs={2.4}>
                    <Box display="flex" justifyContent="center" alignItems="center" gap={0.5} mb={1}>
                        <Typography sx={labelStyle}>Fa</Typography>
                    </Box>
                    {renderCell(fmt(item.FA), '#c0392b')}
                    {renderCell(fmtP(item.PorcentajeFA), '#c0392b')}
                    {renderCell(fmt(item.FAAcumulado), '#c0392b', true)}
                    {renderCell(fmtP(item.PorcentajeFAAcumulado), '#c0392b', true)}
                </Grid>

                <Grid item xs={2.4}>
                    <Box display="flex" justifyContent="center" alignItems="center" gap={0.5} mb={1}>
                        <Typography sx={labelStyle}>Fc</Typography>
                    </Box>
                    {renderCell(fmt(item.FC), '#c0392b')}
                    {renderCell(fmtP(item.PorcentajeFC), '#c0392b')}
                    {renderCell(fmt(item.FCAcumulado), '#27ae60', true)}
                    {renderCell(fmtP(item.PorcentajeFCAcumulado), '#27ae60', true)}
                </Grid>
            </Grid>

            <Box mt={2}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Box flex={1} height={10} bgcolor="#ecf0f1" borderRadius={5} overflow="hidden">
                        <Box height="100%" width={`${Math.min(Math.max(item.PorcentajeProgramadoAcumulado || 0, 0), 100)}%`} bgcolor="#2980b9" />
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold', width: 45, textAlign: 'right', color: '#333' }}>
                        {fmtP(item.PorcentajeProgramadoAcumulado)}
                    </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Box flex={1} height={10} bgcolor="#ecf0f1" borderRadius={5} overflow="hidden">
                        <Box height="100%" width={`${Math.min(Math.max(item.PorcentajeValorizadoAcumulado || 0, 0), 100)}%`} bgcolor="#f39c12" />
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold', width: 45, textAlign: 'right', color: '#333' }}>
                        {fmtP(item.PorcentajeValorizadoAcumulado)}
                    </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                    <Box flex={1} height={10} bgcolor="#ecf0f1" borderRadius={5} overflow="hidden">
                        <Box height="100%" width={`${Math.min(Math.max(item.PorcentajeRealAcumulado || 0, 0), 100)}%`} bgcolor="#1abc9c" />
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold', width: 45, textAlign: 'right', color: '#333' }}>
                        {fmtP(item.PorcentajeRealAcumulado)}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};
